import type { WaveDefinition, WaveGroupDefinition } from "../domain/types";

export interface WaveTickResult {
  readonly spawnEnemyIds: readonly string[];
  readonly waveCompleted: boolean;
  readonly completedWaveNumbers: readonly number[];
  readonly victory: boolean;
}
interface GroupRuntime {
  readonly definition: WaveGroupDefinition;
  spawned: number;
  timerMs: number;
  complete: boolean;
}
interface WaveRuntime {
  readonly definition: WaveDefinition;
  readonly groups: GroupRuntime[];
}

export class WaveManager {
  private waveIndex = -1;
  private activeWaves: WaveRuntime[] = [];
  private activeEnemies = 0;
  private pendingReward = 0;
  constructor(readonly definitions: readonly WaveDefinition[]) {}
  get currentWave(): WaveDefinition | null {
    return this.definitions[this.waveIndex] ?? null;
  }
  get nextWave(): WaveDefinition | null {
    return this.definitions[this.waveIndex + 1] ?? null;
  }
  get currentWaveNumber(): number {
    return this.currentWave?.number ?? 0;
  }
  get totalWaves(): number {
    return this.definitions.length;
  }
  get remainingEnemies(): number {
    return (
      this.activeEnemies +
      this.activeWaves.reduce(
        (waveSum, wave) =>
          waveSum +
          wave.groups.reduce(
            (groupSum, group) => groupSum + group.definition.count - group.spawned,
            0,
          ),
        0,
      )
    );
  }
  get running(): boolean {
    return this.activeWaves.length > 0;
  }
  get activeWaveNumbers(): readonly number[] {
    return this.activeWaves.map((wave) => wave.definition.number);
  }
  startNextWave(): boolean {
    if (this.waveIndex + 1 >= this.definitions.length) return false;
    this.waveIndex += 1;
    const definition = this.currentWave!;
    this.activeWaves.push({
      definition,
      groups: definition.groups.map((group) => ({
        definition: group,
        spawned: 0,
        timerMs: group.delayBeforeMs,
        complete: false,
      })),
    });
    return true;
  }
  tick(deltaMs: number): WaveTickResult {
    const spawnEnemyIds: string[] = [];
    for (const wave of this.activeWaves) {
      for (const group of wave.groups) {
        if (group.complete) continue;
        group.timerMs -= deltaMs;
        while (group.timerMs <= 0 && !group.complete) {
          if (group.spawned < group.definition.count) {
            spawnEnemyIds.push(group.definition.enemyId);
            group.spawned += 1;
            this.activeEnemies += 1;
            group.timerMs +=
              group.spawned === group.definition.count
                ? group.definition.delayAfterMs
                : Math.max(1, group.definition.spawnIntervalMs);
          } else {
            group.complete = true;
          }
        }
      }
    }
    const allSpawned =
      this.activeWaves.length > 0 &&
      this.activeWaves.every((wave) => wave.groups.every((group) => group.complete));
    const waveCompleted = allSpawned && this.activeEnemies === 0;
    const completedWaveNumbers = waveCompleted
      ? this.activeWaves.map((wave) => wave.definition.number)
      : [];
    if (waveCompleted) {
      this.pendingReward += this.activeWaves.reduce(
        (sum, wave) => sum + wave.definition.completionReward,
        0,
      );
      this.activeWaves = [];
    }
    return {
      spawnEnemyIds,
      waveCompleted,
      completedWaveNumbers,
      victory: waveCompleted && this.waveIndex === this.definitions.length - 1,
    };
  }
  registerEnemy(count = 1): void {
    this.activeEnemies += Math.max(0, count);
  }
  resolveEnemy(): boolean {
    if (this.activeEnemies === 0) return false;
    this.activeEnemies -= 1;
    return true;
  }
  claimCompletionReward(): number {
    const reward = this.pendingReward;
    this.pendingReward = 0;
    return reward;
  }
}
